import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import {
  stripTrailingSlash,
  ensureChatKitElementDefined,
} from '../utils/utils';
import { createReportEventParser } from '../utils/reportArtifact';
import { chatBackendUrl, chatkitKey, CHAT_CONFIG } from '../config/config';

const EMPTY_REPORT = { status: "idle", artifact: null, progress: "", error: "" };

export function useChatKit({
  containerRef,
  isOpen,
  sessiontoken,
  theme,
  mountTimeoutMs,
}) {
  const [error, setError] = useState("");
  const [report, setReport] = useState(EMPTY_REPORT);
  const reportIdRef = useRef(null);
  const sessionTokenRef = useRef(sessiontoken);
  const abortRef = useRef(null);
  const clearReport = useCallback(() => {
    reportIdRef.current = null;
    setReport(EMPTY_REPORT);
  }, []);

  const apiUrl = useMemo(() => {
    const base = stripTrailingSlash(chatBackendUrl || "");
    return base ? `${base}/chatkit` : "/chatkit";
  }, []);

  useEffect(() => {
    if (sessionTokenRef.current !== sessiontoken) {
      sessionTokenRef.current = sessiontoken;
      reportIdRef.current = null;
      setReport(EMPTY_REPORT);
    }
    const observerReaders = new Set();
    const cleanup = () => {
      observerReaders.forEach((reader) => reader.cancel().catch(() => {}));
      if (containerRef.current) {
        while (containerRef.current.firstChild) {
          containerRef.current.removeChild(containerRef.current.firstChild);
        }
      }
    };

    // Closing the panel only hides it; ChatKit and its thread history remain mounted.
    if (!sessiontoken) {
      cleanup();
      reportIdRef.current = null;
      setReport(EMPTY_REPORT);
      setError("");
      return;
    }

    let cancelled = false;
    const onReportEvent = (event) => {
      if (cancelled) return;
      if (event.type === "artifact") {
        reportIdRef.current = event.report_id;
        setReport({ status: "ready", artifact: event, progress: "", error: "" });
      } else {
        setReport((current) => ({
          ...current,
          status: current.artifact ? "updating" : "generating",
          progress: event.type === "progress_update" ? event.text : current.progress,
          error: "",
        }));
      }
    };

    const observeReportStream = async (stream) => {
      const reader = stream.getReader();
      observerReaders.add(reader);
      const decoder = new TextDecoder();
      const parser = createReportEventParser(onReportEvent);
      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          parser.push(decoder.decode(value, { stream: true }));
        }
        parser.push(decoder.decode());
        parser.end();
      } catch (e) {
        // This reader is observational: its failure must never reject ChatKit's fetch.
        if (!cancelled) {
          setReport((current) => current.status === "generating" || current.status === "updating"
            ? { ...current, status: "error", error: "No se pudo seguir la generación del reporte." }
            : current);
        }
      } finally {
        observerReaders.delete(reader);
        reader.releaseLock();
      }
    };

    const initChatKit = async () => {
      setError("");
      if (containerRef.current?.firstChild) return;

      const domainKey = chatkitKey;
      if (!domainKey) throw new Error("Missing domainKey from config.");
      await ensureChatKitElementDefined(mountTimeoutMs);

      const mount = containerRef.current;
      if (!mount || cancelled) return;
      while (mount.firstChild) mount.removeChild(mount.firstChild);

      const chatkit = document.createElement("openai-chatkit");
      const ctrl = new AbortController();
      abortRef.current = ctrl;

      const authedFetch = async (input, init = {}) => {
        const req = typeof input === "string" || input instanceof URL
          ? new Request(input, init)
          : input.clone();
        const headers = new Headers(req.headers || {});
        headers.set("sessiontoken", sessiontoken);
        if (!headers.has("content-type")) headers.set("content-type", "application/json");

        const requestedUrl = new URL(req.url, window.location.href);
        const chatUrl = new URL(apiUrl, window.location.href);
        if (reportIdRef.current && requestedUrl.origin === chatUrl.origin &&
          (requestedUrl.pathname === chatUrl.pathname || requestedUrl.pathname.startsWith(`${chatUrl.pathname}/`))) {
          headers.set("x-artifact-report-id", reportIdRef.current);
        }

        const res = await fetch(new Request(req, {
          headers,
          signal: init.signal || ctrl.signal,
        }));
        if (!res.ok) {
          const body = await res.text().catch(() => "");
          throw new Error(`Backend Error: ${res.status} - ${body}`);
        }

        if (res.body && (res.headers.get("content-type") || "").includes("text/event-stream")) {
          try {
            const [chatStream, reportStream] = res.body.tee();
            // Return the first branch immediately; parse only the independent copy.
            observeReportStream(reportStream).catch(() => {});
            return new Response(chatStream, {
              status: res.status,
              statusText: res.statusText,
              headers: res.headers,
            });
          } catch (e) {
            // If observation cannot start, keep the original response for ChatKit.
          }
        }
        return res;
      };

      chatkit.setOptions({
        api: { url: apiUrl, domainKey, fetch: authedFetch },
        theme,
        startScreen: {
          greeting: CHAT_CONFIG.startGreeting,
          prompts: CHAT_CONFIG.prompts,
        },
        composer: {
          placeholder: CHAT_CONFIG.placeholder,
          attachments: { enabled: false },
          models: CHAT_CONFIG.models,
        },
        history: { enabled: true, showDelete: true, showRename: true },
      });
      chatkit.style.width = "100%";
      chatkit.style.height = "100%";
      mount.appendChild(chatkit);
    };

    initChatKit().catch((e) => {
      if (!cancelled) setError(e.message || "Error mounting ChatKit");
    });

    return () => {
      cancelled = true;
      cleanup();
    };
  }, [sessiontoken, theme, mountTimeoutMs, containerRef, apiUrl]);

  return { error, report, clearReport };
}
